// Order Picking → scanner mapping panel.
//
// A GRT driver often works on a GRF scanner (missing or broken GRT
// tablet) and after the evening cut-off most GRF scanners are used for
// reach-truck work. Every mapping here is applied BEFORE the analysis,
// the heatmaps, the per-person table and the rota pack, so the tasks
// and their hours move to the pool that really did the work.

import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeftRight, Download, Plus, Trash2, Clock, Users } from 'lucide-react';
import {
  getResourceMap, saveResourceRule, addResourceMapping, deleteResourceMapping,
  importResourceMapFromPresence,
} from '@/services/api';

const SOURCE_LABEL = { manual: 'Manual', presence: 'Presence', evening_rule: 'Evening rule' };

const ResourceMapPanel = ({ period, onChanged }) => {
  const [map, setMap] = useState(null);
  const [rule, setRule] = useState({ enabled: false, cutoff: '19:00', to_time: '', resources: [] });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState({
    resource: '', treat_as: 'grt', date_from: '', date_to: '', from_time: '', to_time: '',
  });
  const ready = Boolean(period?.from && period?.to);

  const load = useCallback(async () => {
    try {
      const res = await getResourceMap({ date_from: period?.from, date_to: period?.to });
      setMap(res);
      setRule({
        enabled: !!res.rule.enabled,
        cutoff: res.rule.cutoff || '19:00',
        to_time: res.rule.to_time || '',
        resources: res.rule.resources || [],
      });
    } catch (e) {
      toast.error(e.message || 'Could not load the scanner mapping');
    }
  }, [period?.from, period?.to]);

  useEffect(() => { load(); }, [load]);

  const grfCodes = (map?.resources_seen || []).filter((r) => r.base_pool === 'grf').map((r) => r.resource);

  const persist = async (next) => {
    setBusy(true);
    try {
      await saveResourceRule({
        enabled: next.enabled, cutoff: next.cutoff,
        to_time: next.to_time || null, resources: next.resources,
      });
      setRule(next);
      await load();
      onChanged?.();
      toast.success('Scanner mapping updated — re-run Analyse to see it');
    } catch (e) {
      toast.error(e.message || 'Could not save the rule');
    } finally {
      setBusy(false);
    }
  };

  const toggleResource = (code) => {
    const has = rule.resources.includes(code);
    persist({ ...rule, resources: has ? rule.resources.filter((c) => c !== code) : [...rule.resources, code] });
  };

  const addManual = async () => {
    if (!draft.resource || !draft.date_from) {
      toast.error('Pick a scanner and a start date');
      return;
    }
    setBusy(true);
    try {
      await addResourceMapping({ ...draft, date_to: draft.date_to || draft.date_from });
      setDraft({ resource: '', treat_as: 'grt', date_from: '', date_to: '', from_time: '', to_time: '' });
      await load();
      onChanged?.();
      toast.success('Mapping added');
    } catch (e) {
      toast.error(e.message || 'Could not add the mapping');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteResourceMapping(id);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'Could not delete the mapping');
    }
  };

  const importPresence = async (apply) => {
    setBusy(true);
    try {
      const res = await importResourceMapFromPresence({
        date_from: period.from, date_to: period.to, apply,
      });
      if (apply) {
        setPreview(null);
        await load();
        onChanged?.();
        toast.success(`${res.applied} mapping(s) applied from Presence`);
      } else {
        setPreview(res);
      }
    } catch (e) {
      toast.error(e.message || 'Presence import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="resource-map-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowLeftRight className="h-4 w-4 text-[#2D8C8C]" /> Scanner mapping (GRF used as GRT)
            </CardTitle>
            <CardDescription className="text-xs">
              Applied before every calculation on this tab and before the rota pack. Tasks and their
              hours move together, so the pool that really did the work gets the credit.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={() => importPresence(false)} disabled={!ready || busy}
                  data-testid="resource-map-import-presence">
            <Download className="h-4 w-4 mr-1.5" /> Import from Presence
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {map && (
          <p className="text-xs text-muted-foreground" data-testid="resource-map-effect">
            {map.tasks_reassigned > 0
              ? <>This window: <strong className="text-foreground">{map.tasks_reassigned} task(s)</strong> counted as the other pool.</>
              : 'This window: no tasks reassigned yet.'}
          </p>
        )}

        {/* Evening rule */}
        <div className="rounded-lg border border-border p-4 space-y-3" data-testid="resource-map-rule">
          <div className="flex items-start gap-3">
            <Checkbox id="evening-rule" checked={rule.enabled} disabled={busy}
                      data-testid="resource-map-rule-toggle"
                      onCheckedChange={(v) => persist({ ...rule, enabled: !!v })} />
            <div className="space-y-1">
              <Label htmlFor="evening-rule" className="text-sm font-medium text-foreground cursor-pointer">
                After the cut-off, GRF scanners are mainly used as GRT
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Only tasks confirmed at or after the cut-off are moved. Set an end time as well to
                cover a specific shift band instead of the rest of the day.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> From
              </Label>
              <Input type="time" value={rule.cutoff} className="h-8 w-28"
                     data-testid="resource-map-rule-cutoff"
                     onChange={(e) => setRule({ ...rule, cutoff: e.target.value })}
                     onBlur={() => rule.enabled && persist(rule)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">To (optional)</Label>
              <Input type="time" value={rule.to_time} className="h-8 w-28"
                     data-testid="resource-map-rule-to"
                     onChange={(e) => setRule({ ...rule, to_time: e.target.value })}
                     onBlur={() => rule.enabled && persist(rule)} />
            </div>
            <p className="text-[11px] text-muted-foreground pb-1.5">
              {rule.resources.length
                ? `${rule.resources.length} scanner(s) selected`
                : 'No selection = every GRF scanner in the window'}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {grfCodes.length === 0 && (
              <span className="text-[11px] text-muted-foreground">
                No GRF scanner found in this window yet — run Analyse or widen the date range.
              </span>
            )}
            {grfCodes.map((code) => (
              <button key={code} type="button" disabled={busy}
                      data-testid={`resource-map-pick-${code}`}
                      onClick={() => toggleResource(code)}
                      className={`px-2 py-1 rounded-md border text-[11px] font-mono transition-colors ${
                        rule.resources.includes(code)
                          ? 'border-[#2D8C8C] bg-[#2D8C8C]/10 text-[#2D8C8C]'
                          : 'border-border text-muted-foreground hover:border-foreground/30'}`}>
                {code}
              </button>
            ))}
          </div>
        </div>

        {/* Manual fallback */}
        <div className="rounded-lg border border-border p-4 space-y-3" data-testid="resource-map-manual">
          <p className="text-sm font-medium text-foreground">Manual mapping</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Scanner</Label>
              <Select value={draft.resource} onValueChange={(v) => setDraft({ ...draft, resource: v })}>
                <SelectTrigger className="h-8" data-testid="resource-map-draft-resource">
                  <SelectValue placeholder="Pick" />
                </SelectTrigger>
                <SelectContent>
                  {(map?.resources_seen || []).map((r) => (
                    <SelectItem key={r.resource} value={r.resource}>
                      {r.resource} ({r.tasks})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Counts as</Label>
              <Select value={draft.treat_as} onValueChange={(v) => setDraft({ ...draft, treat_as: v })}>
                <SelectTrigger className="h-8" data-testid="resource-map-draft-treatas">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="grt">GRT</SelectItem>
                  <SelectItem value="grf">GRF</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">From date</Label>
              <Input type="date" className="h-8" value={draft.date_from}
                     data-testid="resource-map-draft-from"
                     onChange={(e) => setDraft({ ...draft, date_from: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">To date</Label>
              <Input type="date" className="h-8" value={draft.date_to}
                     data-testid="resource-map-draft-to"
                     onChange={(e) => setDraft({ ...draft, date_to: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Hours (optional)</Label>
              <div className="flex gap-1">
                <Input type="time" className="h-8" value={draft.from_time}
                       data-testid="resource-map-draft-fromtime"
                       onChange={(e) => setDraft({ ...draft, from_time: e.target.value })} />
                <Input type="time" className="h-8" value={draft.to_time}
                       data-testid="resource-map-draft-totime"
                       onChange={(e) => setDraft({ ...draft, to_time: e.target.value })} />
              </div>
            </div>
            <Button onClick={addManual} disabled={busy} className="h-8" data-testid="resource-map-add">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Leave the to-date empty for a single day. One day or a whole range both work.
          </p>
        </div>

        {/* Active mappings */}
        {(map?.mappings || []).length > 0 && (
          <div className="overflow-x-auto" data-testid="resource-map-list">
            <table className="w-full text-xs min-w-[640px]">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="text-left font-medium py-1 pr-3">Scanner</th>
                  <th className="text-left font-medium pr-3">Counts as</th>
                  <th className="text-left font-medium pr-3">Dates</th>
                  <th className="text-left font-medium pr-3">Hours</th>
                  <th className="text-left font-medium pr-3">Person</th>
                  <th className="text-left font-medium pr-3">Source</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {map.mappings.map((m) => (
                  <tr key={m.id} className="border-t border-border" data-testid={`resource-map-row-${m.id}`}>
                    <td className="py-1 pr-3 font-mono text-foreground">{m.resource}</td>
                    <td className="pr-3"><Badge variant="secondary" className="text-[10px]">{m.treat_as.toUpperCase()}</Badge></td>
                    <td className="pr-3 text-muted-foreground">
                      {m.date_from}{m.date_to !== m.date_from ? ` → ${m.date_to}` : ''}
                    </td>
                    <td className="pr-3 text-muted-foreground">
                      {m.from_time || m.to_time ? `${m.from_time || '00:00'}–${m.to_time || '23:59'}` : 'all day'}
                    </td>
                    <td className="pr-3 text-muted-foreground">{m.person || '—'}</td>
                    <td className="pr-3 text-muted-foreground">{SOURCE_LABEL[m.source] || m.source}</td>
                    <td className="text-right">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                              data-testid={`resource-map-delete-${m.id}`} onClick={() => remove(m.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(map?.people || []).length > 0 && (
          <div className="text-xs text-muted-foreground" data-testid="resource-map-people">
            <p className="flex items-center gap-1.5 font-medium text-foreground mb-1">
              <Users className="h-3.5 w-3.5" /> Operators identified from Presence
            </p>
            <div className="flex flex-wrap gap-1.5">
              {map.people.slice(0, 40).map((p, i) => (
                <Badge key={`${p.date}-${p.resource}-${p.from_time || i}`} variant="outline" className="text-[10px] font-normal">
                  {p.date} · {p.resource}
                  {(p.from_time || p.to_time) ? ` ${p.from_time || '00:00'}–${p.to_time || '23:59'}` : ''}
                  {' · '}{p.person || 'unknown'}{p.department ? ` (${p.department})` : ''}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto"
                       data-testid="resource-map-preview-dialog">
          <DialogHeader>
            <DialogTitle>Presence import — review</DialogTitle>
            <DialogDescription>
              {preview?.presence_days} presence day(s), {preview?.entries_scanned} entr(ies) scanned.
              Reach-truck department{preview?.reach_departments?.length ? `: ${preview.reach_departments.join(', ')}` : ' not found'}.
            </DialogDescription>
          </DialogHeader>
          {preview?.note && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3"
               data-testid="resource-map-preview-note">{preview.note}</p>
          )}
          {!!preview?.swaps?.length && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {preview.swaps.length} scanner window(s) will count as the other pool
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="text-left font-medium py-1 pr-3">Date</th>
                      <th className="text-left font-medium pr-3">Scanner</th>
                      <th className="text-left font-medium pr-3">Hours</th>
                      <th className="text-left font-medium pr-3">Counts as</th>
                      <th className="text-left font-medium pr-3">Person</th>
                      <th className="text-left font-medium pr-3">Department</th>
                      <th className="text-left font-medium pr-3">From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.swaps.map((s, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="py-1 pr-3">{s.date}</td>
                        <td className="pr-3 font-mono text-foreground">{s.resource}</td>
                        <td className="pr-3 text-muted-foreground">
                          {s.from_time || s.to_time ? `${s.from_time || '00:00'}–${s.to_time || '23:59'}` : 'whole shift'}
                        </td>
                        <td className="pr-3 uppercase">{s.treat_as}</td>
                        <td className="pr-3">{s.person || '—'}</td>
                        <td className="pr-3 text-muted-foreground">{s.department}</td>
                        <td className="pr-3 text-muted-foreground">{s.field === 'notes' ? 'Notes' : 'Resource'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!!preview?.identifications?.length && (
            <p className="text-xs text-muted-foreground">
              {preview.identifications.length} operator identification(s) will also be stored, so the
              person behind each scanner is known.
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreview(null)} data-testid="resource-map-preview-cancel">
              Cancel
            </Button>
            <Button onClick={() => importPresence(true)} disabled={busy || !preview?.identifications?.length}
                    data-testid="resource-map-preview-apply">
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ResourceMapPanel;
